import { TrackingEvent } from '@acretrader/acremaps-analytics-events';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClone, faInfoCircle } from '@fortawesome/sharp-solid-svg-icons';
import Link from 'next/link';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import Button, { ButtonSize, ButtonVariant } from '@/components/button';
import Checkbox from '@/components/checkbox';
import Typography from '@/components/typography';
import { GLOBAL_TOOLTIP } from '@/constants/constants';
import { LEARN_HOW_TO_USE_EMBEDS_URL } from '@/constants/urls';
import { useThreeDController } from '@/features/3d/3DControllerProvider';
import { convertEmbedUrlToIframe } from '@/features/selectionsPanel/exportMenu/utils';
import { useSelectionsPanelContext } from '@/features/selectionsPanel/selectionsPanelProvider';
import { useAnalytics } from '@/providers/AnalyticsProvider';
import { useFileManagementContext } from '@/providers/FileManagementProvider';
import { useToastContext } from '@/providers/ToastProvider';
import { copyToClipboard } from '@/utils/copyToClipboard';

export const SelectionsEmbedPanel: FC = () => {
  const { activeFile: savedSelection } = useFileManagementContext();
  const { showErrorToast, showSuccessToast } = useToastContext();
  const { terrainEnabled } = useThreeDController();
  const { getEmbedLink, canCopyEmbedLink } = useSelectionsPanelContext();
  const { track } = useAnalytics();

  const [embedLink, setEmbedLink] = useState<string | null | undefined>(null);

  const { watch, register } = useForm({
    defaultValues: {
      make3dModeDefault: terrainEnabled,
      showCropHistory: true,
      showCustomizations: (savedSelection?.customizations?.length ?? 0) > 0,
      showElevation: true,
      showSoils: true,
    },
  });
  const formData = watch();

  // Bust the cached embed link when the user changes the embed settings
  useEffect(() => {
    setEmbedLink(null);
  }, [formData]);

  const getOrCreateEmbedLink = useCallback(async () => {
    const {
      showSoils,
      showCropHistory,
      showCustomizations,
      showElevation,
      make3dModeDefault,
    } = formData;

    if (!canCopyEmbedLink) return undefined;
    let link = embedLink;
    if (!link) {
      link = await getEmbedLink({
        set3dMode: make3dModeDefault,
        showCropHistory,
        showCustomizations,
        showElevation,
        showSoils,
      });
      setEmbedLink(link);
    }
    return link;
  }, [formData, canCopyEmbedLink, embedLink, getEmbedLink]);

  const createAndCopyLinkToClipboard = useCallback(async () => {
    const link = await getOrCreateEmbedLink();
    if (!link) {
      showErrorToast('Error creating embed link');
      return;
    }
    await copyToClipboard(link);
    track({
      data: {},
      event: TrackingEvent.EMBED_COPIED,
    });
    showSuccessToast('Successfully Copied');
  }, [getOrCreateEmbedLink, showErrorToast, showSuccessToast, track]);

  const createAndCopyEmbedIframeToClipboard = useCallback(async () => {
    const link = await getOrCreateEmbedLink();
    if (!link) {
      showErrorToast('Error creating embed link');
      return;
    }
    const embedIframe = convertEmbedUrlToIframe(link);
    await copyToClipboard(embedIframe);
    track({
      data: {},
      event: TrackingEvent.EMBED_COPIED,
    });
    showSuccessToast('Successfully Copied');
  }, [getOrCreateEmbedLink, showErrorToast, showSuccessToast, track]);

  const customizationsTooltip = useMemo(() => {
    if (!savedSelection) return 'Save your map to include drawings';

    if (!savedSelection?.customizations?.length)
      return 'Map must have drawings to include them in an embed';

    return '';
  }, [savedSelection]);

  return (
    <div className="px-4 py-3">
      <Typography variant="h5">Embed Code</Typography>
      <div className="mt-3 flex h-full flex-col gap-y-4">
        <div className="flex flex-col gap-y-2">
          <Typography variant="button2">Live Embed Map</Typography>
          <Typography variant="body2">
            Select the insights you would like to include on your map. Share the
            direct link or embed the map on a listing or website.
          </Typography>
        </div>
        <div className="flex flex-row gap-3">
          <div className="flex flex-col gap-4">
            <Checkbox
              className="space-x-1"
              label="Soils"
              labelVariant="h5"
              {...register('showSoils')}
            />
            <Checkbox
              className="space-x-1"
              label="Elevation"
              labelVariant="h5"
              {...register('showElevation')}
            />
          </div>
          <div className="flex flex-col gap-4">
            <Checkbox
              className="space-x-1"
              label="Crop History"
              labelVariant="h5"
              {...register('showCropHistory')}
            />
            <div
              data-tooltip-content={customizationsTooltip}
              data-tooltip-id={GLOBAL_TOOLTIP}
            >
              <Checkbox
                className="space-x-1"
                disabled={!savedSelection?.customizations?.length}
                label="Drawings"
                labelVariant="h5"
                {...register('showCustomizations')}
              />
            </div>
          </div>
        </div>
        <hr className="border-gray-20" />
        <Checkbox
          className="space-x-1"
          label="Set 3D View as Default"
          labelVariant="h5"
          {...register('make3dModeDefault')}
        />

        <div className="flex flex-col gap-4">
          <Typography variant="button2">URL</Typography>
          <Button
            disabled={!canCopyEmbedLink}
            size={ButtonSize.SMALL}
            variant={ButtonVariant.TERTIARY}
            onClick={createAndCopyLinkToClipboard}
          >
            <Typography variant="button1">
              <FontAwesomeIcon className="mr-3" icon={faClone} />
              Copy Map Link
            </Typography>
          </Button>

          <Typography variant="button2">{'<iframe>'}</Typography>
          <Button
            disabled={!canCopyEmbedLink}
            size={ButtonSize.SMALL}
            variant={ButtonVariant.TERTIARY}
            onClick={createAndCopyEmbedIframeToClipboard}
          >
            <Typography variant="button1">
              <FontAwesomeIcon className="mr-3" icon={faClone} />
              Copy {'<iframe>'}
            </Typography>
          </Button>
        </div>

        <Link
          className="flex flex-nowrap items-center gap-1 text-blue-utility"
          href={LEARN_HOW_TO_USE_EMBEDS_URL}
          target="_blank"
        >
          <FontAwesomeIcon className="mr-1" icon={faInfoCircle} />
          <Typography variant="body2">Learn how to use embeds</Typography>
        </Link>
      </div>
    </div>
  );
};
